import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { FiRefreshCw, FiSearch, FiUsers } from "react-icons/fi";
import { shopClient, eurCents, fmtDate, type ShopCustomerListItem } from "./shopClient";

const PER_PAGE = 50;

/** Back-office listing of Supabase shop customer accounts with search + pagination. */
export default function Customers(): React.ReactElement {
	const history = useHistory();

	const [items, setItems] = useState<ShopCustomerListItem[]>([]);
	const [available, setAvailable] = useState(true);
	const [hasMore, setHasMore] = useState(false);
	const [page, setPage] = useState(1);
	const [search, setSearch] = useState("");
	const [query, setQuery] = useState("");
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	const load = useCallback(async (q: string, p: number) => {
		setLoading(true);
		setError(null);
		try {
			const res = await shopClient.customers.list(q.trim() || undefined, p, PER_PAGE);
			setAvailable(res.available);
			setItems(res.items);
			setHasMore(res.hasMore);
		} catch (e: any) {
			setError(e?.message ?? "Erreur");
			setItems([]);
			setHasMore(false);
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		void load(query, page);
	}, [load, query, page]);

	const submitSearch = useCallback(() => {
		setPage(1);
		setQuery(search);
	}, [search]);

	const refresh = useCallback(() => {
		void load(query, page);
	}, [load, query, page]);

	return (
		<div className="min-h-full p-6">
			<div className="mx-auto w-full max-w-6xl space-y-6">
				<div className="flex flex-wrap items-center gap-3 rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
					<div>
						<p className="text-xs uppercase tracking-widest text-slate-500">Boutique</p>
						<h1 className="text-2xl font-semibold text-slate-900">Clients</h1>
						<p className="mt-1 text-sm text-slate-500">Comptes clients de la boutique (Supabase)</p>
					</div>
					<button
						type="button"
						onClick={refresh}
						disabled={loading}
						className="ml-auto inline-flex items-center gap-2 rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:border-slate-400 disabled:opacity-50"
					>
						<FiRefreshCw className={loading ? "animate-spin" : ""} />
						Actualiser
					</button>
				</div>

				{!available ? (
					<div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
						<div className="flex items-start gap-3 text-slate-600">
							<FiUsers className="mt-0.5 h-5 w-5 text-slate-400" />
							<p className="text-sm">La gestion des clients nécessite Supabase (non configuré).</p>
						</div>
					</div>
				) : (
					<>
						<div className="flex flex-wrap items-center gap-2">
							<div className="relative flex-1 min-w-[16rem]">
								<FiSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
								<input
									value={search}
									onChange={(e) => setSearch(e.target.value)}
									onKeyDown={(e) => {
										if (e.key === "Enter") submitSearch();
									}}
									placeholder="Rechercher par e-mail ou nom…"
									className="w-full rounded-md border border-slate-300 py-2 pl-9 pr-3 text-sm focus:border-slate-500 focus:outline-none"
								/>
							</div>
							<button
								type="button"
								onClick={submitSearch}
								className="inline-flex items-center gap-2 rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50"
							>
								<FiSearch />
								Rechercher
							</button>
						</div>

						{error && (
							<div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>
						)}

						<div className="overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-sm">
							<div className="overflow-x-auto">
								<table className="min-w-full divide-y divide-slate-100 text-sm">
									<thead className="bg-slate-50">
										<tr>
											<th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-widest text-slate-500">E-mail</th>
											<th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-widest text-slate-500">Nom</th>
											<th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-widest text-slate-500">Inscrit</th>
											<th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-widest text-slate-500">Dernière connexion</th>
											<th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-widest text-slate-500">Confirmé</th>
											<th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-widest text-slate-500">Commandes</th>
											<th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-widest text-slate-500">Total dépensé</th>
										</tr>
									</thead>
									<tbody className="divide-y divide-slate-100">
										{loading ? (
											<tr>
												<td colSpan={7} className="px-5 py-10 text-center text-slate-400">Chargement…</td>
											</tr>
										) : items.length === 0 ? (
											<tr>
												<td colSpan={7} className="px-5 py-10 text-center text-slate-400">Aucun client</td>
											</tr>
										) : (
											items.map((c) => (
												<tr
													key={c.id}
													onClick={() => history.push("/customers/" + c.id)}
													className="cursor-pointer hover:bg-slate-50"
												>
													<td className="px-5 py-3 font-medium text-slate-900">{c.email}</td>
													<td className="px-5 py-3 text-slate-600">{c.name || "—"}</td>
													<td className="px-5 py-3 text-slate-600">{fmtDate(c.createdAt)}</td>
													<td className="px-5 py-3 text-slate-600">{fmtDate(c.lastSignInAt)}</td>
													<td className="px-5 py-3">
														<div className="flex flex-wrap items-center gap-1.5">
															{c.confirmed ? (
																<span className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700">Oui</span>
															) : (
																<span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-600">Non</span>
															)}
															{c.banned && (
																<span className="rounded-full bg-rose-50 px-2.5 py-0.5 text-xs font-medium text-rose-600">Banni</span>
															)}
														</div>
													</td>
													<td className="px-5 py-3 text-slate-600">{c.ordersCount}</td>
													<td className="px-5 py-3 font-medium text-slate-900">{eurCents(c.totalSpentCents)}</td>
												</tr>
											))
										)}
									</tbody>
								</table>
							</div>
						</div>

						<div className="flex items-center justify-between">
							<button
								type="button"
								onClick={() => setPage((p) => Math.max(1, p - 1))}
								disabled={loading || page <= 1}
								className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:border-slate-400 disabled:opacity-50"
							>
								Précédent
							</button>
							<span className="text-sm text-slate-500">Page {page}</span>
							<button
								type="button"
								onClick={() => setPage((p) => p + 1)}
								disabled={loading || !hasMore}
								className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:border-slate-400 disabled:opacity-50"
							>
								Suivant
							</button>
						</div>
					</>
				)}
			</div>
		</div>
	);
}
